
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthservicesService } from '../../../services/authservices.service';

@Component({
  selector: 'app-association-manager',
  templateUrl: './association-manager.component.html',
  styleUrls: ['./association-manager.component.css']
})
export class AssociationManagerComponent implements OnInit {
  isHeadQuarters = false;
  loading = false;
  associations: any[] = [];
  listType: string = '';
  skip = 0;
  limit = 10;
  pageSize = 20;
  searchTerm = '';
  candidateCommunities: any[] = [];
  filteredCommunities: any[] = [];
  selectedCommunityIds: string[] = [];
  selectedCommunities: any[] = [];
  selSkip = 0;
  selLimit = 20;
  selLoading = false;
  selAllLoaded = false;
  selQuery = '';
  satelliteModalStep: 'choose' | 'confirm' = 'choose';
  submittingSatellite = false;
  currentUserCommunityId: string = '';

  constructor(
    private router: Router,
    private routeSvc: ActivatedRoute,
    public authService: AuthservicesService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.checkHeadQuartersStatus();
    this.getCurrentUserCommunityId();
    this.routeSvc.queryParams.subscribe(params => {
      this.listType = params['listType'] || '';
      this.loadAssociations();
    });
  }

  isActiveTab(type: 'associations' | 'headquarter' | 'satellite'): boolean {
    if (type === 'associations') return !this.listType || this.listType === 'associations';
    return this.listType === type;
  }

  getReqType(): 'all' | 'headquarter' | 'satellite' {
    if (this.listType === 'headquarter') return 'headquarter';
    if (this.listType === 'satellite') return 'satellite';
    return 'all';
  }

  loadAssociations(): void {
    this.loading = true;
    this.authService.getAssociations(this.getReqType(), this.skip, this.limit).subscribe(
      (response: any) => {
        this.associations = response.data || [];
        // Re-filter communities when associations are loaded/updated
        if (this.candidateCommunities.length > 0) {
          this.filteredCommunities = this.filterAvailableCommunities(this.candidateCommunities);
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error loading associations:', error);
        this.loading = false;
        this.associations = [];
      }
    );
  }

  getFilteredAssociations(): any[] {
    if (this.isActiveTab('headquarter')) {
      // For headquarter tab, only show pending associations (hide approved and rejected)
      return (this.associations || []).filter((assoc: any) => {
        const status = (assoc?.status || '').toLowerCase();
        return status === 'pending';
      });
    }
    // For other tabs (associations, satellite), show all associations
    return this.associations || [];
  }

  openAddSatellite(templateRef: any) {
    // reset selections for a fresh submit flow
    this.selectedCommunityIds = [];
    this.selectedCommunities = [];
    this.satelliteModalStep = 'choose';
    this.openCommunitySelector(templateRef);
  }

  openCommunitySelector(templateRef: any) {
    this.selSkip = 0;
    this.selAllLoaded = false;
    this.selQuery = '';
    this.candidateCommunities = [];
    this.filteredCommunities = [];
    this.getCommunitiesForSelector(true);
    this.modalService.open(templateRef, { scrollable: true });
  }

  onCommunitySearchChange(event: any) {
    this.selQuery = (event?.target?.value || '').trim();
    this.getCommunitiesForSelector(true);
  }

  onSelectorScroll(evt: any) {
    const el = evt?.target;
    if (!el) return;
    const threshold = 80;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.getCommunitiesForSelector();
    }
  }

  getCommunitiesForSelector(reset: boolean = false) {
    if (this.selLoading || this.selAllLoaded) return;
    if (reset) {
      this.selSkip = 0;
      this.selAllLoaded = false;
      this.candidateCommunities = [];
      this.filteredCommunities = [];
    }
    this.selLoading = true;
    const query = 'selectAssociation';
    this.authService
      .getCommunity(this.selSkip, this.selLimit, this.selQuery || this.searchTerm, query)
      .subscribe(
        (res: any) => {
          const base = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : Array.isArray(res?.items)
                ? res.items
                : [];
          const chunk = (base || []).filter((x: any) => !!x && typeof x === 'object' && x._id);
          if (chunk.length < this.selLimit) {
            this.selAllLoaded = true;
          }
          this.candidateCommunities = [...this.candidateCommunities, ...chunk];
          // Filter out current user and already associated communities
          this.filteredCommunities = this.filterAvailableCommunities(this.candidateCommunities);
          this.selSkip += chunk.length;
          this.selLoading = false;
        },
        _ => {
          this.selLoading = false;
        }
      );
  }

  getCurrentUserCommunityId(): void {
    try {
      const profileData = localStorage.getItem('ministry_currentUser');
      if (profileData) {
        const data = JSON.parse(profileData);
        this.currentUserCommunityId = (data?._id || data?.id || '').toString();
      }
    } catch (error) {
      console.error('Error parsing current user from localStorage:', error);
      this.currentUserCommunityId = '';
    }
  }

  getAlreadyAssociatedCommunityIds(): string[] {
    // Get IDs of communities that are already associated (from the associations list)
    return (this.associations || [])
      .map((assoc: any) => {
        // Association can have community._id or communityId
        return assoc?.community?._id || assoc?.communityId || assoc?.community || '';
      })
      .filter((id: string) => !!id);
  }

  filterAvailableCommunities(communities: any[]): any[] {
    const currentUserId = this.currentUserCommunityId;
    const alreadyAssociatedIds = this.getAlreadyAssociatedCommunityIds();

    return communities.filter((c: any) => {
      const communityId = (c?._id || '').toString();
      // Exclude current user's community
      if (currentUserId && communityId === currentUserId) {
        return false;
      }
      // Exclude already associated communities
      if (alreadyAssociatedIds.includes(communityId)) {
        return false;
      }
      return true;
    });
  }

  toggleCommunitySelection(c: any) {
    const id = c?._id;
    const idx = this.selectedCommunityIds.indexOf(id);
    if (idx >= 0) {
      this.selectedCommunityIds.splice(idx, 1);
      this.selectedCommunities = this.selectedCommunities.filter(x => x._id !== id);
    } else {
      this.selectedCommunityIds.push(id);
      this.selectedCommunities = [...this.selectedCommunities, c];
    }
  }

  isCommunitySelected(id: string): boolean {
    return this.selectedCommunityIds.includes(id);
  }

  goToSatelliteConfirm() {
    if (!this.selectedCommunityIds?.length) return;
    this.satelliteModalStep = 'confirm';
  }

  backToSatelliteChoose() {
    this.satelliteModalStep = 'choose';
  }

  submitSelectedSatellites(modalRef?: any) {
    const ids = this.selectedCommunityIds || [];
    if (!ids.length || this.submittingSatellite) return;

    this.submittingSatellite = true;
    let remaining = ids.length;
    let successCount = 0;
    let errorCount = 0;
    const errorMessages: string[] = [];

    ids.forEach(id => {
      this.authService.addAssociationRequest(id).subscribe(
        (res: any) => {
          successCount++;
          remaining--;
          if (remaining === 0) {
            this.submittingSatellite = false;
            this.loadAssociations();
            if (modalRef) modalRef.close('submitted');

            if (errorCount === 0) {
              this.authService.successMessage(
                res?.message || 'Satellite association request sent successfully',
                'success'
              );
            } else {
              this.authService.successMessage(
                `${successCount} request(s) submitted successfully. ${errorCount} failed.`,
                'warning'
              );
            }

            this.selectedCommunityIds = [];
            this.selectedCommunities = [];
            this.satelliteModalStep = 'choose';
          }
        },
        err => {
          errorCount++;
          const errorMsg = err?.error?.message || err?.message || 'Failed to submit satellite request';
          errorMessages.push(errorMsg);
          console.error('Failed to submit satellite request', err);

          remaining--;
          if (remaining === 0) {
            this.submittingSatellite = false;
            this.loadAssociations();
            if (modalRef) modalRef.close('submitted');

            if (successCount === 0) {
              // All failed
              this.authService.successMessage(
                errorMessages[0] || 'Failed to submit satellite requests',
                'error'
              );
            } else {
              // Some succeeded, some failed
              this.authService.successMessage(
                `${successCount} request(s) submitted. ${errorCount} failed: ${errorMessages[0]}`,
                'warning'
              );
            }

            this.selectedCommunityIds = [];
            this.selectedCommunities = [];
            this.satelliteModalStep = 'choose';
          }
        }
      );
    });
  }

  checkHeadQuartersStatus(): void {
    try {
      const profileData = localStorage.getItem('ministry_currentUser');
      if (profileData) {
        const data = JSON.parse(profileData);
        this.isHeadQuarters = !!(data && (data.isHeadQuarters === true || data.isHeadQuarter === true));
      }
    } catch (error) {
      console.error('Error parsing profileData from localStorage:', error);
      this.isHeadQuarters = false;
    }
  }

  switchTab(type: 'associations' | 'headquarter' | 'satellite'): void {
    const listType = type === 'associations' ? null : type;
    this.router.navigate([], {
      relativeTo: this.routeSvc,
      queryParams: { listType },
      queryParamsHandling: 'merge'
    });
  }

  // Remove association (for associations tab and satellite tab)
  removeAssociation(association: any): void {
    // Use association.id first (the association ID), fallback to _id
    // Do NOT use association.community._id (that's the community ID)
    const associationId = association?.id || association?._id;
    if (!associationId) {
      this.authService.successMessage('Invalid association ID', 'error');
      return;
    }

    this.authService.associationRequestAction(associationId, 'remove').subscribe(
      (res: any) => {
        this.authService.successMessage(
          res?.message || 'Association removed successfully',
          'success'
        );
        this.loadAssociations();
      },
      (err: any) => {
        const errorMsg = err?.error?.message || err?.error?.error || err?.message || 'Failed to remove association';
        this.authService.successMessage(errorMsg, 'error');
      }
    );
  }

  // Approve association (for headquarter tab)
  approveAssociation(association: any): void {
    // Use association.id first (the association ID), fallback to _id
    // Do NOT use association.community._id (that's the community ID)
    const associationId = association?.id || association?._id;
    if (!associationId) {
      this.authService.successMessage('Invalid association ID', 'error');
      return;
    }

    this.authService.associationRequestAction(associationId, 'approve').subscribe(
      (res: any) => {
        this.authService.successMessage(
          res?.message || 'Association approved successfully',
          'success'
        );
        this.loadAssociations();
      },
      (err: any) => {
        const errorMsg = err?.error?.message || err?.error?.error || err?.message || 'Failed to approve association';
        this.authService.successMessage(errorMsg, 'error');
      }
    );
  }

  // Reject association (for headquarter tab)
  rejectAssociation(association: any): void {
    // Use association.id first (the association ID), fallback to _id
    // Do NOT use association.community._id (that's the community ID)
    const associationId = association?.id || association?._id;
    if (!associationId) {
      this.authService.successMessage('Invalid association ID', 'error');
      return;
    }

    this.authService.associationRequestAction(associationId, 'reject').subscribe(
      (res: any) => {
        this.authService.successMessage(
          res?.message || 'Association rejected successfully',
          'success'
        );
        this.loadAssociations();
      },
      (err: any) => {
        const errorMsg = err?.error?.message || err?.error?.error || err?.message || 'Failed to reject association';
        this.authService.successMessage(errorMsg, 'error');
      }
    );
  }
}
